/* eslint-disable react/require-default-props */
/* eslint-disable react/destructuring-assignment */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useRef } from 'react';
import {
  normalizeResiduePlanPrimaries,
  normalizeResiduePlansQuotationCompletion,
} from '../../../../../../../../business/proposals/residue-plan';
import { EntityTypeType, UnitOfMeasureType } from '../../../../../../../../models/core/cache.type';
import { Action } from '../../../../../../../../models/core/core.type';
import {
  ProposalResiduePlanCodeEnum,
  ProposalResiduePlanEquipmentType,
  ProposalResiduePlanPackagingType,
  ProposalResiduePlanQuotationType,
  ProposalResiduePlanType,
  ProposalResidueType,
  ProposalServiceType,
} from '../../../../../../../../models/core/proposals.type';
import { buildFakeAuditObject, newGuid } from '../../../../../../../../utils/helper.utils';
import ResiduePlanSummary from '../../../../../components/ResiduePlanSummary';

type ResiduePlanSummaryEditorProps = {
  data: ProposalResidueType;
  isEdit?: boolean;
  referenceData?: {
    entityTypeData?: EntityTypeType[];
    unitOfMeasures: UnitOfMeasureType[];
  };
  services?: ProposalServiceType[];
  zIndex?: number;
  onChange?: (item: ProposalResidueType) => void;
};

export default function ResiduePlanSummaryEditor(props: ResiduePlanSummaryEditorProps) {
  const currentDataRef = useRef(props.data);

  useEffect(() => {
    currentDataRef.current = props.data;
  }, [props.data]);

  const getPlanKey = (plan: ProposalResiduePlanType) =>
    plan.id || plan.idPropostaResiduoPlano?.toString() || plan.idTemporaria || '';

  const getQuotationKey = (quotation: ProposalResiduePlanQuotationType) =>
    quotation.id || quotation.idPropostaResiduoPlanoCotacao?.toString() || quotation.idTemporaria || '';

  const matchesPackagingItem = (
    entry: ProposalResiduePlanPackagingType,
    item: ProposalResiduePlanPackagingType | null
  ) =>
    !!item &&
    ((!!entry.id && !!item.id && `${entry.id}` === `${item.id}`) ||
      (!!entry.idPropostaResiduoPlanoAcondicionamento &&
        !!item.idPropostaResiduoPlanoAcondicionamento &&
        entry.idPropostaResiduoPlanoAcondicionamento === item.idPropostaResiduoPlanoAcondicionamento) ||
      (!!entry.idTemporaria && !!item.idTemporaria && `${entry.idTemporaria}` === `${item.idTemporaria}`));

  const matchesEquipmentItem = (
    entry: ProposalResiduePlanEquipmentType,
    item: ProposalResiduePlanEquipmentType | null
  ) =>
    !!item &&
    ((!!entry.id && !!item.id && `${entry.id}` === `${item.id}`) ||
      (!!entry.idPropostaResiduoPlanoEquipamento &&
        !!item.idPropostaResiduoPlanoEquipamento &&
        entry.idPropostaResiduoPlanoEquipamento === item.idPropostaResiduoPlanoEquipamento) ||
      (!!entry.idTemporaria && !!item.idTemporaria && `${entry.idTemporaria}` === `${item.idTemporaria}`));

  const matchesQuotationChild = (entry: any, itemId: string | number) => {
    const keys = [
      entry?.id,
      entry?.idTemporaria,
      entry?.idPropostaResiduoPlanoCotacaoAcondicionamento,
      entry?.idPropostaResiduoPlanoCotacaoEquipamento,
      entry?.idPropostaResiduoPlanoCotacaoVeiculo,
      entry?.idPropostaResiduoPlanoCotacaoTratamento,
      entry?.idPropostaResiduoPlanoCotacaoDestinoFinal,
      entry?.idPropostaResiduoPlanoAcondicionamento,
      entry?.idPropostaResiduoPlanoEquipamento,
      entry?.idPropostaResiduoPlanoVeiculo,
      entry?.idPropostaResiduoPlanoTratamento,
      entry?.idPropostaResiduoPlanoDestinoFinal,
    ];

    return keys.some((key) => `${key}` === `${itemId}`);
  };

  const createNewQuotation = (plan: ProposalResiduePlanType): ProposalResiduePlanQuotationType => {
    const tempId = newGuid();

    return {
      ...buildFakeAuditObject(),
      id: tempId,
      idTemporaria: tempId,
      action: Action.Add,
      idPropostaResiduoPlanoCotacao: 0,
      idPropostaResiduoPlano: plan.idPropostaResiduoPlano || 0,
      primario: false,
      observacao: null,
      acondicionamentos: [],
      equipamentos: [],
      veiculo: null,
      tratamento: null,
      destinoFinal: null,
      completo: false,
    };
  };

  const createNewPlan = (
    codigoPlanoTipo: ProposalResiduePlanCodeEnum = ProposalResiduePlanCodeEnum.Regular
  ): ProposalResiduePlanType => {
    const tempId = newGuid();
    const nextPlan: ProposalResiduePlanType = {
      ...buildFakeAuditObject(),
      id: tempId,
      idTemporaria: tempId,
      action: Action.Add,
      idPropostaResiduoPlano: 0,
      idPropostaResiduo: props.data.idPropostaResiduo || 0,
      codigoPlanoTipo,
      primario: codigoPlanoTipo === ProposalResiduePlanCodeEnum.Mobilization,
      observacao: null,
      acondicionamentos: [],
      equipamentos: [],
      veiculo: null,
      tratamento: null,
      destinoFinal: null,
      cotacoes: [],
    };

    return nextPlan;
  };

  const invalidatePlanQuotations = (plan: ProposalResiduePlanType) =>
    plan.cotacoes.map((currentQuotation) => {
      if ((currentQuotation.action || Action.Nothing) === Action.Delete) {
        return currentQuotation;
      }

      return {
        ...currentQuotation,
        completo: false,
        action:
          (currentQuotation.action || Action.Nothing) === Action.Nothing
            ? Action.Modify
            : currentQuotation.action,
      };
    });

  const getItemRef = <TRef, TItem>(item: TItem, refItem?: TRef | null) =>
    refItem || ((item as TItem & { refItem?: TRef | null }).refItem ?? null);

  const updateResidue = (updater: (current: ProposalResidueType) => ProposalResidueType) => {
    if (!props.onChange) {
      return;
    }

    const nextData = normalizeResiduePlansQuotationCompletion(
      normalizeResiduePlanPrimaries(updater(currentDataRef.current))
    );
    currentDataRef.current = nextData;
    props.onChange(nextData);
  };

  return (
    <ResiduePlanSummary
      data={props.data}
      isEdit={props.isEdit}
      referenceData={props.referenceData}
      services={props.services}
      zIndex={props.zIndex}
      onAddPlan={(codigoPlanoTipo) =>
        updateResidue((current) => ({
          ...current,
          action: (current.action || Action.Nothing) === Action.Nothing ? Action.Modify : current.action,
          planos: [...current.planos, createNewPlan(codigoPlanoTipo)],
        }))
      }
      onAddPlans={(codigoPlanoTipos) =>
        updateResidue((current) => {
          return {
            ...current,
            action: (current.action || Action.Nothing) === Action.Nothing ? Action.Modify : current.action,
            planos: [
              ...current.planos,
              ...codigoPlanoTipos.map((codigoPlanoTipo) => createNewPlan(codigoPlanoTipo)),
            ],
          };
        })
      }
      onSetPrimaryPlan={(plan) =>
        updateResidue((current) => ({
          ...current,
          action: (current.action || Action.Nothing) === Action.Nothing ? Action.Modify : current.action,
          planos: current.planos.map((entry) => {
            const isTarget = getPlanKey(entry) === getPlanKey(plan);
            let nextAction = entry.action || Action.Nothing;

            if (entry.primario !== isTarget && nextAction === Action.Nothing) {
              nextAction = Action.Modify;
            }

            return {
              ...entry,
              primario: isTarget,
              action: nextAction,
            };
          }),
        }))
      }
      onDeletePlan={(plan) =>
        updateResidue((current) => {
          let nextPlans = current.planos
            .map((currentPlan) => {
              if (getPlanKey(currentPlan) !== getPlanKey(plan)) {
                return currentPlan;
              }

              if (currentPlan.idPropostaResiduoPlano) {
                return { ...currentPlan, action: Action.Delete };
              }

              return null;
            })
            .filter(Boolean) as ProposalResiduePlanType[];

          if (plan.primario) {
            const nextPrimary = nextPlans.find((x) => (x.action || Action.Nothing) !== Action.Delete);
            if (nextPrimary) {
              nextPlans = nextPlans.map((entry) =>
                getPlanKey(entry) === getPlanKey(nextPrimary)
                  ? {
                      ...entry,
                      primario: true,
                      action:
                        (entry.action || Action.Nothing) === Action.Nothing ? Action.Modify : entry.action,
                    }
                  : entry
              );
            }
          }

          return {
            ...current,
            action: (current.action || Action.Nothing) === Action.Nothing ? Action.Modify : current.action,
            planos: nextPlans,
          };
        })
      }
      onPackagingSubmit={(plan, item, _action) => {
        if (!item) return;
        updateResidue((current) => ({
          ...current,
          action: (current.action || Action.Nothing) === Action.Nothing ? Action.Modify : current.action,
          planos: current.planos.map((currentPlan) => {
            if (getPlanKey(currentPlan) !== getPlanKey(plan)) return currentPlan;
            const nextPlan = { ...currentPlan };
            if ((nextPlan.action || Action.Nothing) === Action.Nothing) nextPlan.action = Action.Modify;
            const itemIndex = nextPlan.acondicionamentos.findIndex((entry) =>
              matchesPackagingItem(entry, item)
            );
            nextPlan.acondicionamentos =
              itemIndex >= 0
                ? nextPlan.acondicionamentos.map((entry, index) => (index === itemIndex ? item : entry))
                : [...nextPlan.acondicionamentos, item];
            nextPlan.cotacoes = invalidatePlanQuotations(nextPlan);
            return nextPlan;
          }),
        }));
      }}
      onEquipmentSubmit={(plan, item, action) => {
        if (!item) return;
        updateResidue((current) => ({
          ...current,
          action: (current.action || Action.Nothing) === Action.Nothing ? Action.Modify : current.action,
          planos: current.planos.map((currentPlan) => {
            if (getPlanKey(currentPlan) !== getPlanKey(plan)) return currentPlan;
            const nextPlan = { ...currentPlan };
            if ((nextPlan.action || Action.Nothing) === Action.Nothing) nextPlan.action = Action.Modify;

            let equipmentChanged = false;
            const equipmentIndex = nextPlan.equipamentos.findIndex((entry) =>
              matchesEquipmentItem(entry, item)
            );

            if (equipmentIndex >= 0) {
              const currentItem = nextPlan.equipamentos[equipmentIndex];
              equipmentChanged =
                currentItem.idResiduoEquipamento !== item.idResiduoEquipamento ||
                currentItem.quantidade !== item.quantidade ||
                currentItem.quantidadeIdUnidadeMedida !== item.quantidadeIdUnidadeMedida ||
                (currentItem.observacao || '') !== (item.observacao || '');

              nextPlan.equipamentos = nextPlan.equipamentos.map((entry, index) =>
                index === equipmentIndex ? item : entry
              );
            } else {
              equipmentChanged = true;
              nextPlan.equipamentos = [...nextPlan.equipamentos, item];
            }

            if (action === 'adicionar' || action === 'callback' || action === 'callback-all') {
              nextPlan.cotacoes = nextPlan.cotacoes.map((currentQuotation) => {
                if ((currentQuotation.action || Action.Nothing) === Action.Delete || !equipmentChanged) {
                  return currentQuotation;
                }

                return {
                  ...currentQuotation,
                  completo: false,
                  action:
                    (currentQuotation.action || Action.Nothing) === Action.Nothing
                      ? Action.Modify
                      : currentQuotation.action,
                };
              });
            }

            return nextPlan;
          }),
        }));
      }}
      onVehicleSubmit={(plan, item, _action) => {
        if (!item) return;
        updateResidue((current) => ({
          ...current,
          action: (current.action || Action.Nothing) === Action.Nothing ? Action.Modify : current.action,
          planos: current.planos.map((currentPlan) => {
            if (getPlanKey(currentPlan) !== getPlanKey(plan)) return currentPlan;
            const nextPlan = { ...currentPlan };
            if ((nextPlan.action || Action.Nothing) === Action.Nothing) nextPlan.action = Action.Modify;
            nextPlan.veiculo = item;
            nextPlan.cotacoes = invalidatePlanQuotations(nextPlan);
            return nextPlan;
          }),
        }));
      }}
      onTreatmentSubmit={(plan, item, _action) => {
        if (!item) return;
        updateResidue((current) => ({
          ...current,
          action: (current.action || Action.Nothing) === Action.Nothing ? Action.Modify : current.action,
          planos: current.planos.map((currentPlan) => {
            if (getPlanKey(currentPlan) !== getPlanKey(plan)) return currentPlan;
            const nextPlan = { ...currentPlan };
            if ((nextPlan.action || Action.Nothing) === Action.Nothing) nextPlan.action = Action.Modify;
            nextPlan.tratamento = item;
            nextPlan.cotacoes = invalidatePlanQuotations(nextPlan);
            return nextPlan;
          }),
        }));
      }}
      onDestinationSubmit={(plan, item, _action) => {
        if (!item) return;
        updateResidue((current) => ({
          ...current,
          action: (current.action || Action.Nothing) === Action.Nothing ? Action.Modify : current.action,
          planos: current.planos.map((currentPlan) => {
            if (getPlanKey(currentPlan) !== getPlanKey(plan)) return currentPlan;
            const nextPlan = { ...currentPlan };
            if ((nextPlan.action || Action.Nothing) === Action.Nothing) nextPlan.action = Action.Modify;
            nextPlan.destinoFinal = item;
            nextPlan.cotacoes = invalidatePlanQuotations(nextPlan);
            return nextPlan;
          }),
        }));
      }}
      onPlanItemDelete={(plan, planTab, itemId) =>
        updateResidue((current) => ({
          ...current,
          action: (current.action || Action.Nothing) === Action.Nothing ? Action.Modify : current.action,
          planos: current.planos.map((currentPlan) => {
            if (getPlanKey(currentPlan) !== getPlanKey(plan)) return currentPlan;
            const nextPlan = { ...currentPlan };
            if ((nextPlan.action || Action.Nothing) === Action.Nothing) nextPlan.action = Action.Modify;
            const markDeleted = <T extends { action?: Action }>(entry: T) => {
              if (
                (entry as any)?.idPropostaResiduoPlanoAcondicionamento ||
                (entry as any)?.idPropostaResiduoPlanoEquipamento ||
                (entry as any)?.idPropostaResiduoPlanoVeiculo ||
                (entry as any)?.idPropostaResiduoPlanoTratamento ||
                (entry as any)?.idPropostaResiduoPlanoDestinoFinal
              ) {
                return { ...entry, action: Action.Delete };
              }

              return null;
            };

            const cascadeDeleteQuotationChildren = (quotation: ProposalResiduePlanQuotationType) => {
              const nextQuotation = { ...quotation };

              if ((nextQuotation.action || Action.Nothing) === Action.Nothing) {
                nextQuotation.action = Action.Modify;
              }

              nextQuotation.completo = false;

              switch (planTab) {
                case 'Acondicionamentos':
                  nextQuotation.acondicionamentos = nextQuotation.acondicionamentos
                    .map((entry) => (matchesQuotationChild(entry, itemId) ? markDeleted(entry) : entry))
                    .filter(Boolean) as typeof nextQuotation.acondicionamentos;
                  break;
                case 'Equipamentos':
                  nextQuotation.equipamentos = nextQuotation.equipamentos
                    .map((entry) => (matchesQuotationChild(entry, itemId) ? markDeleted(entry) : entry))
                    .filter(Boolean) as typeof nextQuotation.equipamentos;
                  break;
                case 'Veiculos':
                  if (nextQuotation.veiculo && matchesQuotationChild(nextQuotation.veiculo, itemId)) {
                    nextQuotation.veiculo = markDeleted(
                      nextQuotation.veiculo
                    ) as typeof nextQuotation.veiculo;
                  }
                  break;
                case 'Tratamento':
                case 'Tratamentos':
                  if (nextQuotation.tratamento && matchesQuotationChild(nextQuotation.tratamento, itemId)) {
                    nextQuotation.tratamento = markDeleted(
                      nextQuotation.tratamento
                    ) as typeof nextQuotation.tratamento;
                  }
                  break;
                case 'DestinoFinal':
                case 'DestinoFinais':
                  if (
                    nextQuotation.destinoFinal &&
                    matchesQuotationChild(nextQuotation.destinoFinal, itemId)
                  ) {
                    nextQuotation.destinoFinal = markDeleted(
                      nextQuotation.destinoFinal
                    ) as typeof nextQuotation.destinoFinal;
                  }
                  break;
                default:
                  break;
              }

              return nextQuotation;
            };

            switch (planTab) {
              case 'Acondicionamentos':
                nextPlan.acondicionamentos = nextPlan.acondicionamentos
                  .map((entry) => {
                    const key =
                      entry.id ||
                      entry.idPropostaResiduoPlanoAcondicionamento?.toString() ||
                      entry.idTemporaria;
                    return `${key}` === `${itemId}` ? markDeleted(entry) : entry;
                  })
                  .filter(Boolean) as typeof nextPlan.acondicionamentos;
                break;
              case 'Equipamentos':
                nextPlan.equipamentos = nextPlan.equipamentos
                  .map((entry) => {
                    const key =
                      entry.id || entry.idPropostaResiduoPlanoEquipamento?.toString() || entry.idTemporaria;
                    return `${key}` === `${itemId}` ? markDeleted(entry) : entry;
                  })
                  .filter(Boolean) as typeof nextPlan.equipamentos;
                break;
              case 'Veiculos':
                if (nextPlan.veiculo) {
                  const key =
                    nextPlan.veiculo.id ||
                    nextPlan.veiculo.idPropostaResiduoPlanoVeiculo?.toString() ||
                    nextPlan.veiculo.idTemporaria;
                  if (`${key}` === `${itemId}`)
                    nextPlan.veiculo = markDeleted(nextPlan.veiculo) as typeof nextPlan.veiculo;
                }
                break;
              case 'Tratamento':
                if (nextPlan.tratamento) {
                  const key =
                    nextPlan.tratamento.id ||
                    nextPlan.tratamento.idPropostaResiduoPlanoTratamento?.toString() ||
                    nextPlan.tratamento.idTemporaria;
                  if (`${key}` === `${itemId}`)
                    nextPlan.tratamento = markDeleted(nextPlan.tratamento) as typeof nextPlan.tratamento;
                }
                break;
              case 'DestinoFinal':
                if (nextPlan.destinoFinal) {
                  const key =
                    nextPlan.destinoFinal.id ||
                    nextPlan.destinoFinal.idPropostaResiduoPlanoDestinoFinal?.toString() ||
                    nextPlan.destinoFinal.idTemporaria;
                  if (`${key}` === `${itemId}`)
                    nextPlan.destinoFinal = markDeleted(
                      nextPlan.destinoFinal
                    ) as typeof nextPlan.destinoFinal;
                }
                break;
              default:
                break;
            }

            nextPlan.cotacoes = nextPlan.cotacoes.map(cascadeDeleteQuotationChildren);

            return nextPlan;
          }),
        }))
      }
      onAddQuotation={(plan) =>
        updateResidue((current) => ({
          ...current,
          action: (current.action || Action.Nothing) === Action.Nothing ? Action.Modify : current.action,
          planos: current.planos.map((currentPlan) => {
            if (getPlanKey(currentPlan) !== getPlanKey(plan)) return currentPlan;
            const nextPlan = { ...currentPlan };
            if ((nextPlan.action || Action.Nothing) === Action.Nothing) nextPlan.action = Action.Modify;
            nextPlan.cotacoes = [...nextPlan.cotacoes, createNewQuotation(nextPlan)];
            return nextPlan;
          }),
        }))
      }
      onSetPrimaryQuotation={(plan, quotation) =>
        updateResidue((current) => ({
          ...current,
          planos: current.planos.map((currentPlan) => {
            if (getPlanKey(currentPlan) !== getPlanKey(plan)) return currentPlan;
            const nextPlan = { ...currentPlan };
            if ((nextPlan.action || Action.Nothing) === Action.Nothing) nextPlan.action = Action.Modify;
            nextPlan.cotacoes = nextPlan.cotacoes.map((currentQuotation) => {
              if ((currentQuotation.action || Action.Nothing) === Action.Delete) return currentQuotation;
              const isTarget = getQuotationKey(currentQuotation) === getQuotationKey(quotation);
              let nextAction = currentQuotation.action || Action.Nothing;
              if (isTarget && nextAction === Action.Nothing) nextAction = Action.Modify;
              else if (!isTarget && currentQuotation.primario && nextAction === Action.Nothing)
                nextAction = Action.Modify;
              return { ...currentQuotation, primario: isTarget, action: nextAction };
            });
            return nextPlan;
          }),
        }))
      }
      onDeleteQuotation={(plan, quotation) =>
        updateResidue((current) => ({
          ...current,
          action: (current.action || Action.Nothing) === Action.Nothing ? Action.Modify : current.action,
          planos: current.planos.map((currentPlan) => {
            if (getPlanKey(currentPlan) !== getPlanKey(plan)) return currentPlan;
            const nextPlan = { ...currentPlan };
            if ((nextPlan.action || Action.Nothing) === Action.Nothing) nextPlan.action = Action.Modify;
            nextPlan.cotacoes = nextPlan.cotacoes
              .map((entry) => {
                if (getQuotationKey(entry) !== getQuotationKey(quotation)) return entry;
                if (entry.idPropostaResiduoPlanoCotacao) return { ...entry, action: Action.Delete };
                return null;
              })
              .filter(Boolean) as ProposalResiduePlanQuotationType[];
            return nextPlan;
          }),
        }))
      }
      onQuotationItemDelete={(plan, quotation, quotationTab, itemId) =>
        updateResidue((current) => ({
          ...current,
          action: (current.action || Action.Nothing) === Action.Nothing ? Action.Modify : current.action,
          planos: current.planos.map((currentPlan) => {
            if (getPlanKey(currentPlan) !== getPlanKey(plan)) return currentPlan;
            const nextPlan = { ...currentPlan };
            if ((nextPlan.action || Action.Nothing) === Action.Nothing) nextPlan.action = Action.Modify;
            nextPlan.cotacoes = nextPlan.cotacoes.map((currentQuotation) => {
              if (getQuotationKey(currentQuotation) !== getQuotationKey(quotation)) return currentQuotation;
              const nextQuotation = { ...currentQuotation };
              if ((nextQuotation.action || Action.Nothing) === Action.Nothing)
                nextQuotation.action = Action.Modify;
              nextQuotation.completo = false;
              const markDeleted = <T extends { action?: Action }>(entry: T) => {
                if (
                  (entry as any)?.idPropostaResiduoPlanoCotacaoAcondicionamento ||
                  (entry as any)?.idPropostaResiduoPlanoCotacaoEquipamento ||
                  (entry as any)?.idPropostaResiduoPlanoCotacaoVeiculo ||
                  (entry as any)?.idPropostaResiduoPlanoCotacaoTratamento ||
                  (entry as any)?.idPropostaResiduoPlanoCotacaoDestinoFinal
                ) {
                  return { ...entry, action: Action.Delete };
                }

                return null;
              };

              switch (quotationTab) {
                case 'Acondicionamentos':
                  nextQuotation.acondicionamentos = nextQuotation.acondicionamentos
                    .map((entry) => (matchesQuotationChild(entry, itemId) ? markDeleted(entry) : entry))
                    .filter(Boolean) as typeof nextQuotation.acondicionamentos;
                  break;
                case 'Equipamentos':
                  nextQuotation.equipamentos = nextQuotation.equipamentos
                    .map((entry) => (matchesQuotationChild(entry, itemId) ? markDeleted(entry) : entry))
                    .filter(Boolean) as typeof nextQuotation.equipamentos;
                  break;
                case 'Veiculos':
                  if (nextQuotation.veiculo && matchesQuotationChild(nextQuotation.veiculo, itemId)) {
                    nextQuotation.veiculo = markDeleted(
                      nextQuotation.veiculo
                    ) as typeof nextQuotation.veiculo;
                  }
                  break;
                case 'Tratamentos':
                  if (nextQuotation.tratamento && matchesQuotationChild(nextQuotation.tratamento, itemId)) {
                    nextQuotation.tratamento = markDeleted(
                      nextQuotation.tratamento
                    ) as typeof nextQuotation.tratamento;
                  }
                  break;
                case 'DestinoFinais':
                  if (
                    nextQuotation.destinoFinal &&
                    matchesQuotationChild(nextQuotation.destinoFinal, itemId)
                  ) {
                    nextQuotation.destinoFinal = markDeleted(
                      nextQuotation.destinoFinal
                    ) as typeof nextQuotation.destinoFinal;
                  }
                  break;
                default:
                  break;
              }

              return nextQuotation;
            });
            return nextPlan;
          }),
        }))
      }
      onPackagingQuotationSubmit={(plan, quotation, refItem, item, _action) => {
        if (!item) return;
        const nextRefItem = getItemRef(item, refItem);
        const nextItem = { ...item, refItem: getItemRef(item, refItem) };
        updateResidue((current) => ({
          ...current,
          action: (current.action || Action.Nothing) === Action.Nothing ? Action.Modify : current.action,
          planos: current.planos.map((currentPlan) => {
            if (getPlanKey(currentPlan) !== getPlanKey(plan)) return currentPlan;
            const nextPlan = { ...currentPlan };
            if ((nextPlan.action || Action.Nothing) === Action.Nothing) nextPlan.action = Action.Modify;
            if (nextRefItem) {
              const planItemIndex = nextPlan.acondicionamentos.findIndex((entry) =>
                matchesPackagingItem(entry, nextRefItem)
              );
              nextPlan.acondicionamentos =
                planItemIndex >= 0
                  ? nextPlan.acondicionamentos.map((entry, index) =>
                      index === planItemIndex ? nextRefItem : entry
                    )
                  : [...nextPlan.acondicionamentos, nextRefItem];
            }
            nextPlan.cotacoes = nextPlan.cotacoes.map((currentQuotation) => {
              if (getQuotationKey(currentQuotation) !== getQuotationKey(quotation)) return currentQuotation;
              const nextQuotation = { ...currentQuotation };
              if ((nextQuotation.action || Action.Nothing) === Action.Nothing)
                nextQuotation.action = Action.Modify;
              const itemIndex = nextQuotation.acondicionamentos.findIndex(
                (entry) =>
                  (!!entry.id && !!nextItem.id && `${entry.id}` === `${nextItem.id}`) ||
                  (!!entry.idPropostaResiduoPlanoCotacaoAcondicionamento &&
                    !!nextItem.idPropostaResiduoPlanoCotacaoAcondicionamento &&
                    entry.idPropostaResiduoPlanoCotacaoAcondicionamento ===
                      nextItem.idPropostaResiduoPlanoCotacaoAcondicionamento) ||
                  (!!entry.idTemporaria &&
                    !!nextItem.idTemporaria &&
                    `${entry.idTemporaria}` === `${nextItem.idTemporaria}`)
              );
              nextQuotation.acondicionamentos =
                itemIndex >= 0
                  ? nextQuotation.acondicionamentos.map((entry, index) =>
                      index === itemIndex ? nextItem : entry
                    )
                  : [...nextQuotation.acondicionamentos, nextItem];
              nextQuotation.completo = false;
              return nextQuotation;
            });
            return nextPlan;
          }),
        }));
      }}
      onEquipmentQuotationSubmit={(plan, quotation, refItem, item, _action) => {
        if (!item) return;
        const nextRefItem = getItemRef(item, refItem);
        const nextItem = { ...item, refItem: getItemRef(item, refItem) };
        updateResidue((current) => ({
          ...current,
          action: (current.action || Action.Nothing) === Action.Nothing ? Action.Modify : current.action,
          planos: current.planos.map((currentPlan) => {
            if (getPlanKey(currentPlan) !== getPlanKey(plan)) return currentPlan;
            const nextPlan = { ...currentPlan };
            if ((nextPlan.action || Action.Nothing) === Action.Nothing) nextPlan.action = Action.Modify;
            if (nextRefItem) {
              const planItemIndex = nextPlan.equipamentos.findIndex((entry) =>
                matchesEquipmentItem(entry, nextRefItem)
              );
              nextPlan.equipamentos =
                planItemIndex >= 0
                  ? nextPlan.equipamentos.map((entry, index) =>
                      index === planItemIndex ? nextRefItem : entry
                    )
                  : [...nextPlan.equipamentos, nextRefItem];
            }
            nextPlan.cotacoes = nextPlan.cotacoes.map((currentQuotation) => {
              if (getQuotationKey(currentQuotation) !== getQuotationKey(quotation)) return currentQuotation;
              const nextQuotation = { ...currentQuotation };
              if ((nextQuotation.action || Action.Nothing) === Action.Nothing)
                nextQuotation.action = Action.Modify;
              const itemIndex = nextQuotation.equipamentos.findIndex(
                (entry) =>
                  (!!entry.id && !!nextItem.id && `${entry.id}` === `${nextItem.id}`) ||
                  (!!entry.idPropostaResiduoPlanoCotacaoEquipamento &&
                    !!nextItem.idPropostaResiduoPlanoCotacaoEquipamento &&
                    entry.idPropostaResiduoPlanoCotacaoEquipamento ===
                      nextItem.idPropostaResiduoPlanoCotacaoEquipamento) ||
                  (!!entry.idTemporaria &&
                    !!nextItem.idTemporaria &&
                    `${entry.idTemporaria}` === `${nextItem.idTemporaria}`)
              );
              nextQuotation.equipamentos =
                itemIndex >= 0
                  ? nextQuotation.equipamentos.map((entry, index) => (index === itemIndex ? nextItem : entry))
                  : [...nextQuotation.equipamentos, nextItem];
              nextQuotation.completo = false;
              return nextQuotation;
            });
            return nextPlan;
          }),
        }));
      }}
      onVehicleQuotationSubmit={(plan, quotation, refItem, item, _action) => {
        if (!item) return;
        const nextRefItem = getItemRef(item, refItem);
        const nextItem = { ...item, refItem: getItemRef(item, refItem) };
        updateResidue((current) => ({
          ...current,
          action: (current.action || Action.Nothing) === Action.Nothing ? Action.Modify : current.action,
          planos: current.planos.map((currentPlan) => {
            if (getPlanKey(currentPlan) !== getPlanKey(plan)) return currentPlan;
            const nextPlan = { ...currentPlan };
            if ((nextPlan.action || Action.Nothing) === Action.Nothing) nextPlan.action = Action.Modify;
            if (nextRefItem) {
              nextPlan.veiculo = nextRefItem;
            }
            nextPlan.cotacoes = nextPlan.cotacoes.map((currentQuotation) => {
              if (getQuotationKey(currentQuotation) !== getQuotationKey(quotation)) return currentQuotation;
              const nextQuotation = { ...currentQuotation };
              if ((nextQuotation.action || Action.Nothing) === Action.Nothing)
                nextQuotation.action = Action.Modify;
              nextQuotation.veiculo = nextItem;
              nextQuotation.completo = false;
              return nextQuotation;
            });
            return nextPlan;
          }),
        }));
      }}
      onTreatmentQuotationSubmit={(plan, quotation, refItem, item, _action) => {
        if (!item) return;
        const nextRefItem = getItemRef(item, refItem);
        const nextItem = { ...item, refItem: nextRefItem };
        updateResidue((current) => ({
          ...current,
          action: (current.action || Action.Nothing) === Action.Nothing ? Action.Modify : current.action,
          planos: current.planos.map((currentPlan) => {
            if (getPlanKey(currentPlan) !== getPlanKey(plan)) return currentPlan;
            const nextPlan = { ...currentPlan };
            if ((nextPlan.action || Action.Nothing) === Action.Nothing) nextPlan.action = Action.Modify;
            if (nextRefItem) {
              nextPlan.tratamento = nextRefItem;
            }
            nextPlan.cotacoes = nextPlan.cotacoes.map((currentQuotation) => {
              if (getQuotationKey(currentQuotation) !== getQuotationKey(quotation)) return currentQuotation;
              const nextQuotation = { ...currentQuotation };
              if ((nextQuotation.action || Action.Nothing) === Action.Nothing)
                nextQuotation.action = Action.Modify;
              nextQuotation.tratamento = nextItem;
              nextQuotation.completo = false;
              return nextQuotation;
            });
            return nextPlan;
          }),
        }));
      }}
      onDestinationQuotationSubmit={(plan, quotation, refItem, item, _action) => {
        if (!item) return;
        const nextRefItem = getItemRef(item, refItem);
        const nextItem = { ...item, refItem: nextRefItem };
        updateResidue((current) => ({
          ...current,
          action: (current.action || Action.Nothing) === Action.Nothing ? Action.Modify : current.action,
          planos: current.planos.map((currentPlan) => {
            if (getPlanKey(currentPlan) !== getPlanKey(plan)) return currentPlan;
            const nextPlan = { ...currentPlan };
            if ((nextPlan.action || Action.Nothing) === Action.Nothing) nextPlan.action = Action.Modify;
            if (nextRefItem) {
              nextPlan.destinoFinal = nextRefItem;
            }
            nextPlan.cotacoes = nextPlan.cotacoes.map((currentQuotation) => {
              if (getQuotationKey(currentQuotation) !== getQuotationKey(quotation)) return currentQuotation;
              const nextQuotation = { ...currentQuotation };
              if ((nextQuotation.action || Action.Nothing) === Action.Nothing)
                nextQuotation.action = Action.Modify;
              nextQuotation.destinoFinal = nextItem;
              nextQuotation.completo = false;
              return nextQuotation;
            });
            return nextPlan;
          }),
        }));
      }}
    />
  );
}
